/**
 * AdminVoiceFillModal — shared voice-fill shell for admin surfaces.
 *
 * Drives record → AI extract → preview → confirm for any /api/ai/voice-fill/*
 * endpoint. Callers supply the endpoint, friendly title, hint, and a render
 * function for the structured preview.
 *
 *   <AdminVoiceFillModal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     endpoint="/ai/voice-fill/incident-review"
 *     extra={{ incident_id: id }}
 *     title="Voice incident review"
 *     hint="Dictate your review and action plan."
 *     limitMs={120_000}
 *     renderPreview={(r) => (<IncidentReviewPreview result={r} />)}
 *     onConfirm={(r) => savePayload(r)}
 *     confirmLabel="Save review"
 *     testIdPrefix="voice-incident-review"
 *   />
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { Mic, Square, Loader2, Sparkles, X, Volume2, AlertTriangle, Check } from "lucide-react";
import useVoiceRecorder from "@/lib/useVoiceRecorder";

export default function AdminVoiceFillModal({
  open,
  onClose,
  endpoint,
  extra = {},
  title,
  hint,
  examples,
  limitMs = 90 * 1000,
  filenamePrefix = "admin",
  renderPreview,
  onConfirm,
  confirmLabel = "Use this",
  testIdPrefix = "voice-fill",
}) {
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const rec = useVoiceRecorder({
    endpoint,
    extra,
    filenamePrefix,
    limitMs,
    onResult: (data) => setResult(data),
  });

  const close = () => {
    rec.reset();
    setResult(null);
    setSaving(false);
    onClose?.();
  };

  const confirm = async () => {
    if (!result || !onConfirm) return;
    setSaving(true);
    try {
      await onConfirm(result);
      close();
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-16 overflow-y-auto"
      data-testid={`${testIdPrefix}-modal`}
    >
      <div className="card-surface w-full max-w-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-clinical-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "#7C3AED1a", color: "#7C3AED" }}
            >
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-clinical-text">{title}</h2>
              <p className="text-xs text-clinical-subtext">
                {rec.isIdle && !result && (hint || "Tap to record. AI will structure your dictation.")}
                {rec.isRecording && `Listening · ${rec.mmss}`}
                {rec.isProcessing && "Understanding…"}
                {result && "Review and confirm"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
            aria-label="Close"
            data-testid={`${testIdPrefix}-close`}
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {!result && (
            <div className="flex flex-col items-center gap-3 py-4">
              {rec.isIdle && (
                <div className="text-center space-y-2">
                  <Volume2 className="mx-auto h-10 w-10 text-clinical-subtext/50" />
                  <p className="text-xs text-clinical-subtext">
                    {Math.round(limitMs / 1000)}s limit per recording.
                  </p>
                  {Array.isArray(examples) && examples.length > 0 && (
                    <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50/60 p-3 text-left"
                      data-testid={`${testIdPrefix}-examples`}
                    >
                      <div className="label-eyebrow text-purple-700 mb-1.5">Try saying</div>
                      <ul className="space-y-1.5 text-xs text-purple-900">
                        {examples.slice(0, 4).map((ex, i) => (
                          <li key={i} className="flex gap-1.5 leading-snug">
                            <span className="shrink-0 select-none">"</span>
                            <span className="italic">{ex}</span>
                            <span className="shrink-0 select-none">"</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {rec.isRecording && (
                <div className="flex flex-col items-center gap-2">
                  <div className="relative h-20 w-20">
                    <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-ping" />
                    <div className="relative h-20 w-20 rounded-full bg-purple-500 flex items-center justify-center">
                      <Mic className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div
                    className="text-2xl font-mono tabular-nums text-clinical-text"
                    data-testid={`${testIdPrefix}-timer`}
                  >
                    {rec.mmss}
                  </div>
                </div>
              )}
              {rec.isProcessing && (
                <Loader2 className="h-10 w-10 animate-spin text-clinical-accent" />
              )}
              <button
                type="button"
                onClick={rec.toggle}
                disabled={rec.isProcessing}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                  rec.isRecording
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-clinical-accent text-white hover:opacity-90"
                } disabled:opacity-50`}
                data-testid={`${testIdPrefix}-${rec.isRecording ? "stop" : "start"}`}
              >
                {rec.isRecording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                {rec.isRecording ? "Stop" : rec.isProcessing ? "Processing…" : "Start"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid={`${testIdPrefix}-results`}>
              {result.transcript && (
                <div className="rounded-lg bg-clinical-bg/60 p-3">
                  <div className="label-eyebrow mb-1">You said</div>
                  <p className="text-sm text-clinical-text">"{result.transcript}"</p>
                </div>
              )}

              {renderPreview ? renderPreview(result) : (
                <pre className="text-xs whitespace-pre-wrap rounded-lg bg-clinical-bg/60 p-3 text-clinical-text">
                  {JSON.stringify(result, null, 2)}
                </pre>
              )}

              {(result.warnings || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={12} /> Heads up
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {result.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {result && (
          <div className="border-t border-clinical-border px-5 py-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                setResult(null);
                rec.reset();
              }}
              className="text-sm text-clinical-subtext hover:text-clinical-text"
              data-testid={`${testIdPrefix}-redo`}
            >
              Try again
            </button>
            {onConfirm && (
              <button
                type="button"
                onClick={confirm}
                disabled={saving}
                className="btn-primary disabled:opacity-50"
                data-testid={`${testIdPrefix}-confirm`}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {confirmLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
